/**
 * Accolade names, for the admin panel.
 *
 * ## The boundary this file sits on
 *
 * `user_accolades` stores an `accolade_id` and nothing else, so an id like
 * `cs2_major_cologne_2026` is all the database knows. The names, the art and the
 * earning rules live in the DESKTOP app, which is a separate repository:
 *
 *   C:\StreamNook\src\utils\seasonalAccolades.ts      (seasonal + cake day)
 *   C:\StreamNook\src\components\settings\ProfileOverview.tsx  (the rest)
 *
 * That app is the SOURCE OF TRUTH. This file is a copy for display, and a copy
 * of a list in another repo is exactly the shape that has bitten this project
 * before: the 8.2.0 `channel_points_farmed` rename updated the desktop and left
 * this site querying the old column, which took the whole admin dashboard down.
 *
 * So the rules for this file are:
 *
 *   1. IDS are the contract, not the labels. An id here that the desktop has
 *      retired is harmless; an id the desktop has ADDED simply shows as its raw
 *      id, which is why `accoladeLabel` falls back to a humanised id instead of
 *      dropping the row. A member's earned accolade is never hidden just because
 *      this list is behind.
 *   2. Nothing here is load-bearing. It is prose for a panel. No query, no gate
 *      and no grant reads it, so a stale label costs a slightly worse admin
 *      screen and nothing else.
 *   3. STORED accolades only. The desktop also shows ~20 threshold accolades
 *      (Chatterbox at 1,000 messages, Marathoner at 100 hours, and so on) that
 *      are DERIVED from stats and never written to `user_accolades`. Those are
 *      deliberately not duplicated here: copying twenty thresholds across a repo
 *      boundary is twenty things to drift, and the panel already shows the
 *      underlying numbers right next to this section, which is better
 *      information than a badge asserting the same thing.
 */

/** How an accolade is come by. Drives grouping, not styling. */
export type AccoladeKind = 'seasonal' | 'holiday' | 'event' | 'secret' | 'milestone';

export interface AccoladeDef {
  id: string;
  label: string;
  kind: AccoladeKind;
  /** One line on what earns it. Shown on hover. */
  hint: string;
}

/** Ported 2026-09-19. Keep ids exact; labels are display-only. */
export const ACCOLADES: AccoladeDef[] = [
  // Holidays: narrow windows, earned by having the app open on the day.
  { id: 'new_year', label: "New Year's", kind: 'holiday', hint: 'Open StreamNook around New Year (Dec 31 to Jan 1)' },
  { id: 'valentines', label: "Valentine's", kind: 'holiday', hint: "Open StreamNook around Valentine's Day" },
  { id: 'st_patricks', label: "St. Patrick's", kind: 'holiday', hint: "Open StreamNook around St. Patrick's Day" },
  { id: 'april_fools', label: "April Fools'", kind: 'holiday', hint: 'Open StreamNook on April Fools' },
  { id: 'may_fourth', label: 'May the 4th', kind: 'holiday', hint: 'Open StreamNook on May 4' },
  { id: 'halloween', label: 'Halloween', kind: 'holiday', hint: 'Open StreamNook around Halloween' },
  { id: 'thanksgiving', label: 'Turkey Day', kind: 'holiday', hint: 'Open StreamNook around Thanksgiving' },
  { id: 'winter_holiday', label: 'Winter Holiday', kind: 'holiday', hint: 'Open StreamNook around the winter holidays' },

  // Seasons: broad windows, collected just by being around.
  { id: 'spring', label: 'Spring', kind: 'seasonal', hint: 'Open StreamNook during spring' },
  { id: 'summer', label: 'Summer', kind: 'seasonal', hint: 'Open StreamNook during summer' },
  { id: 'fall', label: 'Fall', kind: 'seasonal', hint: 'Open StreamNook during fall' },
  { id: 'winter', label: 'Winter', kind: 'seasonal', hint: 'Open StreamNook during winter' },

  // Per-member, so it cannot be a fixed window.
  { id: 'cake_day', label: 'Cake Day', kind: 'milestone', hint: 'Open StreamNook on your Twitch cake day' },

  // Limited events.
  { id: 'cs2_major_cologne_2026', label: 'CS2 Major Cologne 2026', kind: 'event', hint: 'Watched Counter-Strike in StreamNook during the CS2 Major' },
  { id: 'semiquincentennial_2026', label: 'Semiquincentennial', kind: 'event', hint: 'Open StreamNook around the Fourth of July, 2026' },
  { id: 'grand_finale_2026', label: 'Grand Finale', kind: 'event', hint: 'Stayed a while under the anniversary sky' },

  // Secret, earned by a specific act rather than a threshold.
  { id: 'insomniac', label: 'Restless', kind: 'secret', hint: 'The test notification really wishes you would stop' },
];

const BY_ID = new Map(ACCOLADES.map((a) => [a.id, a]));

/** Turn `cs2_major_cologne_2026` into `Cs2 major cologne 2026`.
 *
 *  Only reached for an id the desktop has added and this file has not. Showing a
 *  readable guess beats showing nothing, because the fact that MATTERS (this
 *  member earned something) survives a stale label. */
function humanise(id: string): string {
  const words = id.replace(/[_-]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : id;
}

export function accoladeLabel(id: string): string {
  return BY_ID.get(id)?.label ?? humanise(id);
}

export function accoladeDef(id: string): AccoladeDef | null {
  return BY_ID.get(id) ?? null;
}

/** Known first (in catalog order), then anything unrecognised, so a stale list
 *  degrades by appending rather than by scrambling the order. */
export function sortAccoladeIds(ids: string[]): string[] {
  const order = new Map(ACCOLADES.map((a, i) => [a.id, i]));
  return [...ids].sort((a, b) => {
    const ia = order.get(a);
    const ib = order.get(b);
    if (ia != null && ib != null) return ia - ib;
    if (ia != null) return -1;
    if (ib != null) return 1;
    return a.localeCompare(b);
  });
}
